import { getApiErrorMessage } from "@/api/utils";
import { useFileRecordExport } from "@/panes/file-record-detail/FileRecordExportContext";
import { FileRecordSelectionActionOverlay } from "@/panes/file-record-detail/FileRecordSelectionActionOverlay";
import { useDomTextSelectionAction } from "@/panes/file-record-detail/useDomTextSelectionAction";
import { usePushFileRecordTextSelectionToChat } from "@/panes/file-record-detail/usePushFileRecordTextSelectionToChat";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  fileRecordVersionKey,
  Markdown,
  useFileRecordText,
} from "@cohesive/ui-components";
import { useEffect, useRef } from "react";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

export function MarkdownContent({ record }: { record: FileRecord }) {
  const { setMarkdownSource } = useFileRecordExport();
  const { previewFetchUrl, isPreviewing } = useFileRecordVersionPreview();
  const contentRef = useRef<HTMLDivElement>(null);
  const fetchUrl = previewFetchUrl ?? record.url;
  const {
    data: text = null,
    isPending,
    error: loadError,
  } = useFileRecordText({
    url: fetchUrl,
    versionKey: previewFetchUrl ?? fileRecordVersionKey(record),
  });
  const isLoading = !!fetchUrl && isPending;
  const error = loadError ? getApiErrorMessage(loadError) : null;

  const { enabled: textSelectionEnabled, pushSelection } =
    usePushFileRecordTextSelectionToChat({
      fileRecordId: record.id,
      fileRecordName: getFileRecordName(record),
    });
  const canReferenceSelectionInChat = textSelectionEnabled && !isPreviewing;

  const { selectedText, anchorRect, clearSelection } =
    useDomTextSelectionAction({
      enabled: canReferenceSelectionInChat && text != null,
      rootRef: contentRef,
    });

  useEffect(() => {
    setMarkdownSource(text);
    return () => setMarkdownSource(null);
  }, [text, setMarkdownSource]);

  if (!fetchUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Markdown not available.
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        className={cn(
          "h-full transition-opacity duration-300",
          !isLoading && text != null ? "opacity-100" : "opacity-0",
        )}
      >
        {text != null && (
          <div
            ref={contentRef}
            className="min-h-0 max-h-none h-full overflow-y-auto px-6 py-4 prose prose-sm dark:prose-invert max-w-none"
          >
            <Markdown>{text}</Markdown>
          </div>
        )}
      </div>
      {isLoading && <LoadingOverlay />}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {error}
        </div>
      )}
      {canReferenceSelectionInChat && (
        <FileRecordSelectionActionOverlay
          selectedText={selectedText}
          anchorRect={anchorRect}
          onActivate={pushSelection}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}
